import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useOrgStore } from '@/stores/orgStore'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import { sourcesApi } from '@/api/clients/sourcesApi'
import { connectorsApi } from '@/api/clients/connectorsApi'
import type { ImportResult, BulkImportResult } from '@/api/types'
import { PageHeader, Card, Button, Field, SelectInput } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { Spinner } from '@/components/common/State'
import { formatDate } from '@/components/common/format'

// Built-in connector catalogue (design §5.1). Only the Module 1 connector is
// interactive here; others describe how their data reaches qtz-risk.
const CATALOG = [
  {
    key: 'discovery',
    name: 'Module 1 — Discovery',
    icon: 'travel_explore',
    desc: 'Import CBOM (crypto assets) + business-context (CMDB) from qtz-discovery.',
    status: 'Built-in',
    statusVariant: 'success' as const,
  },
  {
    key: 'cyclonedx',
    name: 'CycloneDX Upload',
    icon: 'upload_file',
    desc: 'Drag-drop a CycloneDX 1.7 CBOM/SBOM file into an inventory source.',
    status: 'Built-in',
    statusVariant: 'success' as const,
  },
  {
    key: 'cmdb',
    name: 'CMDB (business-context)',
    icon: 'storage',
    desc: 'Sync unit / criticality / owners / regulatory scope onto inventories (via Module 1).',
    status: 'Built-in',
    statusVariant: 'success' as const,
  },
  {
    key: 'sbom',
    name: 'SBOM (software)',
    icon: 'category',
    desc: 'Software components are not modelled in P1 — covered conceptually by CBOM.',
    status: 'Not in P1',
    statusVariant: 'neutral' as const,
  },
]

export default function Connectors() {
  const { currentOrgId: orgId } = useOrgStore()
  const [showM1, setShowM1] = useState(false)
  const [showBulk, setShowBulk] = useState(false)

  return (
    <div>
      <PageHeader
        title="Connectors"
        subtitle="Built-in data connectors — import crypto & business-context into qtz-risk"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CATALOG.map((c) => (
          <Card key={c.key}>
            <div className="flex items-start gap-3">
              <span className="material-icons text-primary dark:text-secondary text-2xl">
                {c.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">
                    {c.name}
                  </h3>
                  <Badge label={c.status} variant={c.statusVariant} />
                </div>
                <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark">{c.desc}</p>
                {c.key === 'discovery' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button variant="primary" icon="sync" onClick={() => setShowBulk(true)}>
                      Sync data
                    </Button>
                    <Button icon="tune" onClick={() => setShowM1(true)}>
                      Import into one inventory
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Platform-managed note */}
      <p className="mt-4 text-xs text-subtle-light dark:text-subtle-dark">
        Module 1 endpoint & credentials are platform-managed (backend env
        <code className="mx-1">QTZ_DISCOVERY_BASE_URL</code>/<code className="mx-1">…_SERVICE_SECRET</code>).
        Import is scoped to a target inventory.
      </p>

      {showM1 && <M1Panel orgId={orgId} onClose={() => setShowM1(false)} />}
      {showBulk && <BulkImportModal orgId={orgId} onClose={() => setShowBulk(false)} />}
    </div>
  )
}

function BulkImportModal({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [confirmed, setConfirmed] = useState(false)
  const [result, setResult] = useState<BulkImportResult | null>(null)

  const run = useMutation({
    mutationFn: () => connectorsApi.syncData(orgId),
    onSuccess: (r) => {
      setResult(r)
      qc.invalidateQueries({ queryKey: ['inventories', orgId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
    },
  })

  return (
    <Modal
      title="Sync data from qtz-discovery"
      onClose={onClose}
      wide
      footer={
        result ? (
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            View dashboard
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!confirmed || run.isPending}
              onClick={() => run.mutate()}
            >
              {run.isPending ? 'Syncing…' : 'Sync now'}
            </Button>
          </>
        )
      }
    >
      {!result && (
        <div className="space-y-4">
          <p className="text-sm text-text-light dark:text-text-dark">
            Full sync with qtz-discovery: inventories and sources are added, updated, and
            removed to match discovery exactly, and each source's CBOM is re-ingested + re-scored.
            Idempotent — safe to re-run (no duplicates). Manual data is never touched.
          </p>
          <div className="p-3 rounded-xl bg-warning/10 text-xs text-text-light dark:text-text-dark">
            <span className="material-icons text-warning text-sm align-middle mr-1">warning</span>
            Removes mirrored inventories/sources (and their assets) that no longer exist in
            discovery. Runs synchronously and re-scores everything — may take a while.
          </div>
          <label className="flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded"
            />
            I understand this may take a while.
          </label>
          {run.isPending && <Spinner label="Syncing with discovery…" />}
          {run.error && <p className="text-sm text-error">{(run.error as Error).message}</p>}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="p-3 rounded-xl bg-success/10 text-sm text-text-light dark:text-text-dark">
            Inventories: <strong>{result.inventories_created}</strong> added ·{' '}
            <strong>{result.inventories_updated}</strong> updated ·{' '}
            <strong>{result.inventories_removed ?? 0}</strong> removed. Sources:{' '}
            <strong>{result.sources_created ?? 0}</strong> added ·{' '}
            <strong>{result.sources_removed ?? 0}</strong> removed ·{' '}
            <strong>{result.assets_ingested}</strong> assets ingested.
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                  <th className="px-3 py-2 font-medium">Inventory</th>
                  <th className="px-3 py-2 font-medium">Assets</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light dark:divide-border-dark">
                {result.results.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-text-light dark:text-text-dark">{r.name}</td>
                    <td className="px-3 py-2">{r.assets_ingested}</td>
                    <td className="px-3 py-2">
                      {r.error ? (
                        <span className="text-error" title={r.error}>error</span>
                      ) : (
                        <Badge label={r.created ? 'created' : 'updated'} variant="success" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  )
}

function M1Panel({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [invId, setInvId] = useState('')
  const [confirmAll, setConfirmAll] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)

  const inventories = useQuery({
    queryKey: ['inventories', orgId],
    queryFn: () => inventoriesApi.list(orgId),
    enabled: !!orgId,
  })
  const selected = invId || inventories.data?.[0]?.id || ''

  const sources = useQuery({
    queryKey: ['sources', orgId, selected],
    queryFn: () => sourcesApi.list(orgId, selected),
    enabled: !!orgId && !!selected,
  })

  const runImport = useMutation({
    mutationFn: (mode: 'all' | 'discovery' | 'cmdb') =>
      mode === 'all'
        ? connectorsApi.importAll(orgId, selected)
        : mode === 'discovery'
          ? connectorsApi.importCbom(orgId, selected)
          : connectorsApi.importContext(orgId, selected),
    onSuccess: (r) => {
      setResult(r)
      setConfirmAll(false)
      qc.invalidateQueries({ queryKey: ['sources', orgId, selected] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
    },
  })

  const discoverySources = (sources.data ?? []).filter((s) => s.source_type === 'discovery')

  return (
    <Modal title="Module 1 — Discovery connector" onClose={onClose} wide>
      {inventories.isLoading ? (
        <Spinner />
      ) : inventories.data?.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-subtle-light dark:text-subtle-dark">
            No inventory yet — create one to import Module 1 data into.
          </p>
          <Button variant="primary" icon="add" onClick={() => navigate('/inventories')}>
            Go to Inventories
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label="Target inventory">
            <SelectInput value={selected} onChange={(e) => setInvId(e.target.value)}>
              {inventories.data?.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.name}
                </option>
              ))}
            </SelectInput>
          </Field>

          {/* Volume warning */}
          <div className="p-3 rounded-xl bg-warning/10 text-xs text-text-light dark:text-text-dark">
            <span className="material-icons text-warning text-sm align-middle mr-1">warning</span>
            The Module 1 org export can be very large (100k+ crypto assets). A full import
            ingests and re-scores all of them and may take a while.
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              icon="cloud_download"
              disabled={runImport.isPending}
              onClick={() => setConfirmAll(true)}
            >
              Import all (CBOM + CMDB)
            </Button>
            <Button
              icon="key"
              disabled={runImport.isPending}
              onClick={() => runImport.mutate('discovery')}
            >
              CBOM only
            </Button>
            <Button
              icon="badge"
              disabled={runImport.isPending}
              onClick={() => runImport.mutate('cmdb')}
            >
              Business-context only
            </Button>
          </div>

          {runImport.isPending && <Spinner label="Importing from Module 1…" />}
          {runImport.error && (
            <p className="text-sm text-error">{(runImport.error as Error).message}</p>
          )}
          {result && (
            <div className="p-3 rounded-xl bg-success/10 text-sm text-text-light dark:text-text-dark">
              Imported <strong>{result.assets_ingested}</strong> assets
              {result.context_synced ? ' · business-context synced' : ''}
              {result.context_error ? ` · context: ${result.context_error}` : ''}.
              <button
                className="ml-2 text-primary dark:text-secondary hover:underline"
                onClick={() => navigate('/dashboard')}
              >
                View dashboard
              </button>
            </div>
          )}

          {/* Discovery-managed sources */}
          <div>
            <p className="label-sm text-subtle-light dark:text-subtle-dark mb-2">
              Discovery-managed sources in this inventory
            </p>
            {discoverySources.length === 0 ? (
              <p className="text-xs text-subtle-light dark:text-subtle-dark">
                None yet — created automatically on first import.
              </p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {discoverySources.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 py-2 text-sm">
                    <span className="material-icons text-subtle-light dark:text-subtle-dark text-base">
                      travel_explore
                    </span>
                    <span className="flex-1 text-text-light dark:text-text-dark truncate">
                      {s.name}
                    </span>
                    <span className="text-xs text-subtle-light dark:text-subtle-dark">
                      synced {formatDate(s.last_ingested_at)}
                    </span>
                    <RefreshButton orgId={orgId} invId={selected} sourceId={s.id} onDone={() => sources.refetch()} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {confirmAll && (
        <Modal
          title="Confirm full import"
          onClose={() => setConfirmAll(false)}
          footer={
            <>
              <Button onClick={() => setConfirmAll(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => runImport.mutate('all')}>
                Import all
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-light dark:text-text-dark">
            This imports the full Module 1 CBOM for the org into the selected inventory and
            re-scores every asset. It may take a while for large orgs. Proceed?
          </p>
        </Modal>
      )}
    </Modal>
  )
}

function RefreshButton({
  orgId,
  invId,
  sourceId,
  onDone,
}: {
  orgId: string
  invId: string
  sourceId: string
  onDone: () => void
}) {
  const refresh = useMutation({
    mutationFn: () => sourcesApi.refresh(orgId, invId, sourceId),
    onSuccess: onDone,
  })
  return (
    <Button variant="ghost" icon="refresh" disabled={refresh.isPending} onClick={() => refresh.mutate()} title="Re-pull from Module 1">
      {refresh.isPending ? '…' : ''}
    </Button>
  )
}
